import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, PackageSearch, SlidersHorizontal, ArrowLeft } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ProductCard } from "@/components/shop/ProductCard";
import { PurchaseOptionDialog } from "@/components/shop/PurchaseOptionDialog";
import { ShopFilters, emptyFilters, type Filters } from "@/components/shop/ShopFilters";
import { listProducts } from "@/lib/shop.functions";
import { findShopGroup, SORT_OPTIONS, standardDisplayPrice, type Product, type SortValue } from "@/lib/shop";
import { useCompare, useWishlist } from "@/lib/shop-prefs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

export const Route = createFileRoute("/shop/category/$slug")({
  component: CategoryPage,
  head: ({ params }) => {
    const label = params.slug
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    const title = `${label} | Rehabilitation Shop | N S Physiotherapy`;
    const description = `Shop ${label.toLowerCase()} selected by qualified physiotherapists at N S Physiotherapy in Centurion. Standard or physiotherapist guided purchase options available.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: `https://nsphysiotherapy.co.za/shop/category/${params.slug}` }],
    };
  },
});

function CategoryPage() {
  const { slug } = Route.useParams();
  const fn = useServerFn(listProducts);
  const { data, isPending, isError } = useQuery({ queryKey: ["shop", "products"], queryFn: () => fn() });
  const products = (data ?? []) as Product[];

  const group = useMemo(() => findShopGroup(products, slug), [products, slug]);
  const groupProducts = group?.products ?? [];

  const [sort, setSort] = useState<SortValue>("featured");
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [selected, setSelected] = useState<Product | null>(null);
  const wishlist = useWishlist();
  const compare = useCompare();

  const facets = useMemo(() => {
    const brands = new Set<string>();
    const sizes = new Set<string>();
    const colours = new Set<string>();
    let priceMax = 1000;
    for (const p of groupProducts) {
      if (p.brand) brands.add(p.brand);
      p.sizes?.forEach((s) => sizes.add(s));
      p.colours?.forEach((c) => colours.add(c));
      priceMax = Math.max(priceMax, p.standard_price);
    }
    return {
      brands: [...brands].sort(),
      sizes: [...sizes].sort(),
      colours: [...colours].sort(),
      priceMax: Math.ceil(priceMax / 50) * 50,
    };
  }, [groupProducts]);

  const results = useMemo(() => {
    let list = [...groupProducts];
    const f = filters;
    if (f.brands.length) list = list.filter((p) => f.brands.includes(p.brand ?? ""));
    if (f.sizes.length) list = list.filter((p) => p.sizes?.some((s) => f.sizes.includes(s)));
    if (f.colours.length) list = list.filter((p) => p.colours?.some((c) => f.colours.includes(c)));
    if (f.sides.length) list = list.filter((p) => p.sides?.some((s) => f.sides.includes(s)));
    if (f.inStock) list = list.filter((p) => p.stock_quantity > 0);
    if (f.onSale) list = list.filter((p) => p.on_sale);
    if (f.physioRecommended) list = list.filter((p) => p.physio_recommended);
    if (f.maxPrice != null) list = list.filter((p) => standardDisplayPrice(p) <= f.maxPrice!);

    switch (sort) {
      case "newest":
        list.sort((a, b) => +new Date(b.created_at) - +new Date(a.created_at));
        break;
      case "price-asc":
        list.sort((a, b) => standardDisplayPrice(a) - standardDisplayPrice(b));
        break;
      case "price-desc":
        list.sort((a, b) => standardDisplayPrice(b) - standardDisplayPrice(a));
        break;
      case "name-asc":
        list.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "name-desc":
        list.sort((a, b) => b.name.localeCompare(a.name));
        break;
      default:
        list.sort((a, b) => Number(b.featured) - Number(a.featured) || a.sort_order - b.sort_order);
    }
    return list;
  }, [groupProducts, filters, sort]);

  const filterPanel = <ShopFilters filters={filters} setFilters={setFilters} facets={facets} />;

  return (
    <PageShell>
      <PageHero
        eyebrow="Physio Shop"
        title={group?.name ?? "Category"}
        subtitle={group?.subtitle ?? "Rehabilitation products selected by qualified physiotherapists."}
      />

      <Breadcrumbs
        items={[{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }, { label: group?.name ?? "Category" }]}
      />

      <section className="container-page py-8">
        <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 max-h-[calc(100dvh-8rem)] overflow-y-auto pr-2">{filterPanel}</div>
          </aside>

          <div className="min-w-0">
            <div className="mb-6 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
              <p className="min-w-0 truncate text-xs text-muted-foreground">
                {isPending ? "Loading" : `${results.length} product${results.length === 1 ? "" : "s"}`}
              </p>
              <div className="flex shrink-0 items-center gap-2">
                <Sheet>
                  <SheetTrigger asChild>
                    <button className="inline-flex h-9 items-center gap-2 rounded-full border border-border px-4 text-xs font-semibold text-primary hover:bg-accent lg:hidden">
                      <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
                    </button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[85vw] overflow-y-auto sm:max-w-sm">
                    <SheetHeader>
                      <SheetTitle className="font-display text-primary">Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">{filterPanel}</div>
                  </SheetContent>
                </Sheet>
                <label className="sr-only" htmlFor="sort">
                  Sort products
                </label>
                <select
                  id="sort"
                  value={sort}
                  onChange={(e) => setSort(e.target.value as SortValue)}
                  className="h-9 rounded-full border border-border bg-background px-3 text-xs font-semibold text-primary outline-none focus:border-primary"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {isPending ? (
              <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : isError ? (
              <p className="text-sm text-destructive">We could not load the catalogue. Please refresh and try again.</p>
            ) : results.length === 0 ? (
              <div className="rounded-2xl border border-border bg-secondary/30 p-10 text-center">
                <PackageSearch className="mx-auto h-10 w-10 text-muted-foreground/60" />
                <h2 className="mt-4 font-display text-lg font-semibold text-primary">No products in this category yet</h2>
                <Link
                  to="/shop"
                  className="mt-5 inline-flex h-10 items-center gap-2 rounded-full border border-border px-5 text-xs font-semibold text-primary hover:bg-accent"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to shop
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                {results.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onAdd={setSelected}
                    wishlisted={wishlist.has(p.id)}
                    onWishlist={wishlist.toggle}
                    compared={compare.has(p.id)}
                    onCompare={compare.toggle}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <PurchaseOptionDialog product={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} />
    </PageShell>
  );
}
